import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";

import { useAssetLookup } from "../../api/useAssetLookup";
import { formatPickRate } from "../../format/formatPickRate";
import type { AnalysisBuild, AnalysisPurchase } from "../../model/Analysis";
import { AssetIcon } from "../AssetIcon";
import { BUILD_KIND_TITLE } from "../../model/BUILD_KIND_TITLE";

const ALTERNATIVE_LINE = msg({
  id: "analysis.build.alternative.line",
  message:
    "{share} of strong {hero} players build this way — you are compared with them, not with everyone",
});
const ALTERNATIVE_FINE = msg({
  id: "analysis.build.alternative.fine",
  message: "Buying is in order for this build",
});
const ALTERNATIVE_FIX = msg({
  id: "analysis.build.alternative.fix",
  message: "Below — what to fix for this build",
});
const UNUSUAL_TITLE = msg({
  id: "analysis.build.unusual.title",
  message: "Strong {hero} players almost never build like this",
});
const UNUSUAL_LINE = msg({
  id: "analysis.build.unusual.line",
  message:
    "Before 25:00 — {count, plural, one {# item} other {# items}} taken by at most {max} of strong {hero} players",
});

type BuildCardProps = {
  build: AnalysisBuild;
  heroId: number;
  purchases: readonly AnalysisPurchase[];
  // Находок закупа по этой сборке нет — «по этой сборке закуп в порядке».
  fine: boolean;
};

// Сборка игрока над ключевыми предметами, по доске `BuildFinal.dc.html`: своя сборка сильных
// (зелёная) — с кем сравниваем и всё ли по ней в порядке; «сильные так не собирают»
// (жёлтая) — какие покупки выбиваются. У обычной сборки плашки нет.
export const BuildCard = ({ build, heroId, purchases, fine }: BuildCardProps) => {
  const { _, i18n } = useLingui();
  const findItem = useAssetLookup("items");
  const hero = useAssetLookup("heroes")(heroId);
  if (build.outcome === "alternative") {
    return (
      <section
        aria-label={_({ ...BUILD_KIND_TITLE, values: { kind: build.kind } })}
        data-build="alternative"
        className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-[10px] border border-good/40 bg-good/10 px-4 py-3.5"
      >
        <span className="flex shrink-0 gap-1">
          {build.key_item_ids.slice(0, 3).map((itemId) => (
            <AssetIcon
              key={itemId}
              asset={findItem(itemId)}
              size={44}
              className="rounded-[10px] border-2 border-border-strong"
            />
          ))}
        </span>
        <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-0.5">
          <span className="text-base font-semibold text-foreground">
            {_({ ...BUILD_KIND_TITLE, values: { kind: build.kind } })}
          </span>
          <span className="text-[13px] text-muted">
            {_({
              ...ALTERNATIVE_LINE,
              values: {
                share: formatPickRate(build.share ?? 0, i18n.locale),
                hero: hero.name,
              },
            })}
          </span>
          <span className={cn("text-sm", fine ? "text-good" : "text-muted")}>
            {_(fine ? ALTERNATIVE_FINE : ALTERNATIVE_FIX)}
          </span>
        </div>
      </section>
    );
  }
  if (build.outcome !== "unusual") {
    return null;
  }
  const picks = new Map(purchases.map((purchase) => [purchase.item_id, purchase.pick_rate ?? 0]));
  const odd = build.odd_item_ids.map((itemId) => ({
    itemId,
    asset: findItem(itemId),
    pick: picks.get(itemId) ?? 0,
  }));
  const max = Math.max(0, ...odd.map((item) => item.pick));
  return (
    <section
      aria-label={_({ ...UNUSUAL_TITLE, values: { hero: hero.name } })}
      data-build="unusual"
      className="flex flex-col gap-2.5 rounded-[10px] border border-warn/40 bg-warn/10 px-4 py-3.5"
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-base font-semibold text-warn">
          {_({ ...UNUSUAL_TITLE, values: { hero: hero.name } })}
        </span>
        {odd.length > 0 && (
          <span className="text-[13px] text-foreground-soft">
            {_({
              ...UNUSUAL_LINE,
              values: {
                count: odd.length,
                max: formatPickRate(max, i18n.locale),
                hero: hero.name,
              },
            })}
          </span>
        )}
      </div>
      {odd.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {odd.map(({ itemId, asset, pick }) => (
            <li
              key={itemId}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background py-0.5 pr-2.5 pl-0.5 text-xs whitespace-nowrap text-foreground-soft"
            >
              <AssetIcon asset={asset} size={24} decorative className="rounded-full" />
              {asset.name}
              <span className="text-subtle">{formatPickRate(pick, i18n.locale)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
